import {
  BadgeAlert,
  BadgeCheck,
  CheckCircle,
  CircleAlert,
  Copy,
  Edit,
  Ellipsis,
  Frown,
  Link2,
  Mail,
  Phone,
  Plus,
  Save,
  Trash2,
  UserCheck2,
  UserCircle2,
  UserRoundPlusIcon,
  Users,
  X,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  DeletionForm,
  deletionSchema,
  NewBranchForm,
  newBranchSchema,
} from "@/schema/schema";
import {
  Select,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectContent,
} from "@/components/ui/select";
import { countries } from "@/constants/countryCodes";
import { states } from "@/constants/states";
import { useBranch } from "@/hooks/useBranch";
import LoadingIndicator from "./LoadingIndicator";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  BranchMember,
  ChangeActiveBranch,
  UserActiveBranch,
} from "@/types/branch";
import { useCurrentActiveBranch } from "@/hooks/useCurrentActiveBranch";
import { useAuth } from "@/hooks/useAuth";

const Branch = () => {
  const { currentActiveBranch } = useCurrentActiveBranch();
  const [selectedBranch, setSelectedBranch] = useState<string>("");
  const [searchBranch, setSearchBranch] = useState<string>("");
  const { branches, isLoading } = useBranch(selectedBranch, searchBranch);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isRemoving, setIsRemoving] = useState<boolean>(false);
  const [isBranchDetailOpen, setIsBranchDetailOpen] = useState<boolean>(false);
  const [isAddBranchMemberOpen, setIsAddBranchMemberOpen] =
    useState<boolean>(false);
  const [isChangingBranch, setIsChangingBranch] = useState<boolean>(false);
  const [isSelfServiceUrlOpen, setIsSelfServiceUrlOpen] = useState<boolean>(false);

  const handleModal = (modalType: string, selectedBranchId: string) => {
    setSelectedBranch(selectedBranchId);

    switch (modalType) {
      case "ViewMembers": {
        setIsOpen(true);
        break;
      }
      case "RemoveBranch": {
        setIsRemoving(true);
        break;
      }
      case "BranchDetail": {
        setIsBranchDetailOpen(true);
        break;
      }
      case "AddBranchMember": {
        setIsAddBranchMemberOpen(true);
        break;
      }
      case "SwitchActiveBranch": {
        setIsChangingBranch(true);
        break;
      }
      default:
        break;
    }
  };

  return (
    <div className="min-h-screen flex items-start justify-center p-4">
      <div className="w-full space-y-4">
        {/* Branches List */}
        <Card>
          <CardHeader>
            <CardTitle>Branches</CardTitle>
            <CardDescription>Manage your branches</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="w-full bg-orange-50 border border-orange-100 text-slate-500 dark:bg-orange-950/20 dark:border-orange-900/50 dark:text-slate-400 p-2.5 rounded-md select-none">
              <p className="text-sm font-bold">
                Note:{" "}
                <span className="font-normal">
                  Click on the inactive button to switch active branch.
                </span>
              </p>
            </div>
            <div className="w-full flex gap-4">
              <Input
                placeholder="Search branch code..."
                onChange={(e) => setSearchBranch(e.target.value)}
              />
              <Button
                size="sm"
                type="button"
                variant="outline"
                onClick={() => setIsSelfServiceUrlOpen(true)}
              >
                <Link2 />
                Self-Service URL
              </Button>
              {currentActiveBranch?.role === "owner" && (
                <Button
                  size="sm"
                  type="button"
                  onClick={() => handleModal("BranchDetail", "CreateBranch")}
                >
                  <Plus />
                  New Branch
                </Button>
              )}
            </div>

            <div className="w-full h-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 text-sm gap-4">
              {isLoading ? (
                <div className="col-span-3 p-4">
                  <LoadingIndicator />
                </div>
              ) : branches.length === 0 ? (
                <div className="col-span-3 p-4 flex flex-col items-center justify-center gap-1 text-gray-400">
                  <Frown size={50} />
                  <span>No branch found</span>
                </div>
              ) : (
                branches.map((branch) => (
                  <div
                    key={branch.id}
                    className={`w-full h-full border rounded-md shadow flex flex-col p-4 gap-2 ${
                      currentActiveBranch?.branchId === branch.id
                        ? "border-green-500"
                        : ""
                    } text-gray-500`}
                  >
                    <p className="font-semibold text-md text-black">
                      {branch.prefixCode}
                    </p>
                    <span>{branch.name}</span>
                    {branch.prefixCode !== "HQ" && (
                      <>
                        <span className="flex gap-2.5 items-center leading-none">
                          <Mail size={15} />
                          {branch.email}
                        </span>
                        <span className="flex gap-2.5 items-center leading-none">
                          <Phone size={15} />
                          {branch.contactNumber}
                        </span>
                      </>
                    )}
                    <Button
                      className="w-fit px-0 font-normal"
                      variant="ghost"
                      size="sm"
                      disabled={branch.totalMembers === 0}
                      onClick={() => handleModal("ViewMembers", branch.id)}
                    >
                      <Users size={15} />
                      {branch.totalMembers} members
                    </Button>
                    <div className="w-full h-full flex items-end justify-between">
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className={`px-0 font-normal ${
                          currentActiveBranch?.branchId === branch.id
                            ? "text-green-700"
                            : "text-orange-600"
                        } `}
                        disabled={currentActiveBranch?.branchId === branch.id}
                        onClick={() =>
                          handleModal("SwitchActiveBranch", branch.id)
                        }
                      >
                        {currentActiveBranch?.branchId === branch.id ? (
                          <>
                            <BadgeCheck fill="green" className="text-white" />
                            Active
                          </>
                        ) : (
                          <>
                            <BadgeAlert fill="orange" className="text-white" />
                            Inactive
                          </>
                        )}
                      </Button>
                      <div className="w-fit flex gap-2.5">
                        {currentActiveBranch?.role === "owner" && (
                          <Button
                            size="icon"
                            variant="outline"
                            onClick={() =>
                              handleModal("AddBranchMember", branch.id)
                            }
                            type="button"
                          >
                            <UserRoundPlusIcon />
                          </Button>
                        )}
                        {branch.prefixCode !== "HQ" && (
                          <>
                            <Button
                              size="icon"
                              variant="outline"
                              onClick={() =>
                                handleModal("BranchDetail", branch.id)
                              }
                              type="button"
                            >
                              <Ellipsis />
                            </Button>
                            {currentActiveBranch?.role === "owner" && (
                              <Button
                                type="button"
                                size="icon"
                                variant="destructive"
                                onClick={() =>
                                  handleModal("RemoveBranch", branch.id)
                                }
                              >
                                <Trash2 />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        <BranchDetail
          isOpen={isBranchDetailOpen}
          onClose={() => setIsBranchDetailOpen(false)}
          selectedBranch={selectedBranch}
          currentActiveBranch={currentActiveBranch}
        />

        <BranchMembers
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          selectedBranch={selectedBranch}
          currentActiveBranch={currentActiveBranch}
        />

        <AddBranchMembers
          isOpen={isAddBranchMemberOpen}
          onClose={() => setIsAddBranchMemberOpen(false)}
          selectedBranch={selectedBranch}
        />

        <RemoveBranch
          isOpen={isRemoving}
          onClose={() => setIsRemoving(false)}
          selectedBranch={selectedBranch}
        />

        <SwitchActiveBranch
          isOpen={isChangingBranch}
          onClose={() => setIsChangingBranch(false)}
          selectedBranch={selectedBranch}
        />

        <SelfServiceUrlDialog
          isOpen={isSelfServiceUrlOpen}
          onClose={() => setIsSelfServiceUrlOpen(false)}
          currentActiveBranch={currentActiveBranch}
        />
      </div>
    </div>
  );
};

type BranchModalProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedBranch: string;
  currentActiveBranch?: UserActiveBranch;
};

const BranchDetail = ({
  isOpen,
  onClose,
  selectedBranch,
  currentActiveBranch,
}: BranchModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [enableEdit, setEnableEdit] = useState<boolean>(false);
  const {
    addBranch,
    updateBranch,
    isCreating,
    branchDetails,
    isLoadingBranch,
  } = useBranch(selectedBranch);
  const defaultValues = {
    prefixCode: "",
    name: "",
    contactNumber: "",
    email: "",
    address: {
      addressLine0: "",
      addressLine1: undefined,
      addressLine2: undefined,
      cityName: "",
      postalZone: "",
      state: "",
      country: "MYS",
    },
  };
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<NewBranchForm>({
    resolver: zodResolver(newBranchSchema),
    defaultValues: defaultValues,
  });

  useEffect(() => {
    const defaultView = !["", "CreateBranch"].includes(selectedBranch);
    setEnableEdit(defaultView);
    if (!["", "CreateBranch"].includes(selectedBranch)) {
      reset(branchDetails);
    } else {
      reset(defaultValues);
    }
  }, [selectedBranch, branchDetails, isLoadingBranch, reset]);

  const handleEnableEdit = () => setEnableEdit((prev) => (prev = !prev));

  const onSubmit = async (data: NewBranchForm) => {
    const { address, ...branchDetail } = data;
    const formData = {
      ...branchDetail,
      address: {
        addressLine0: address.addressLine0,
        addressLine1: address.addressLine1 === "" ? null : address.addressLine1,
        addressLine2: address.addressLine2 === "" ? null : address.addressLine2,
        postalZone: address.postalZone === "" ? null : address.postalZone,
        cityName: address.cityName,
        state: address.state,
        country: address.country,
      },
    };
    if (selectedBranch === "CreateBranch") {
      await addBranch(formData, {
        onSuccess: () => {
          toast({
            title: "Branch Created",
            description: `Successfully added ${data.prefixCode}.`,
            icon: (
              <CheckCircle
                size={30}
                className="animate-spin-once text-green-400"
              />
            ),
            duration: 5000,
          });
          queryClient.invalidateQueries({ queryKey: ["branches"] });
          reset();
          onClose();
        },
        onError: (response) => {
          let errorMessage = `Failed to add ${data.prefixCode}. Please try again.`;

          if (response instanceof AxiosError) {
            errorMessage = response.response?.data.message;
          }

          toast({
            title: "Creation Failed",
            variant: "destructive",
            description: errorMessage,
            icon: (
              <CircleAlert
                size={30}
                className="animate-spin-once text-red-400"
              />
            ),
            duration: 4000,
          });
        },
      });
    } else {
      await updateBranch(formData, {
        onSuccess: () => {
          toast({
            title: "Branch Updated",
            description: `Successfully update ${data.prefixCode}.`,
            icon: (
              <CheckCircle
                size={30}
                className="animate-spin-once text-green-400"
              />
            ),
            duration: 5000,
          });
          queryClient.invalidateQueries({
            predicate: (query) =>
              query.queryKey[0] === "branches" ||
              query.queryKey[0] === "branchDetails",
          });
          reset();
          setEnableEdit(true);
          onClose();
        },
        onError: (response) => {
          let errorMessage = `Failed to update ${data.prefixCode}. Please try again.`;

          if (response instanceof AxiosError) {
            errorMessage = response.response?.data.message;
          }

          toast({
            title: "Update Failed",
            variant: "destructive",
            description: errorMessage,
            icon: (
              <CircleAlert
                size={30}
                className="animate-spin-once text-red-400"
              />
            ),
            duration: 4000,
          });
        },
      });
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="h-full overflow-y-auto flex flex-col gap-2.5">
        <SheetHeader>
          <SheetTitle>Branch Details</SheetTitle>
          <SheetDescription>
            {!["", "CreateBranch"].includes(selectedBranch)
              ? "View or update branch informations"
              : "Complete all required details"}
          </SheetDescription>
        </SheetHeader>
        {isLoadingBranch ? (
          <LoadingIndicator />
        ) : (
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="grid grid-cols-2 gap-4"
          >
            <div className="col-span-2">
              <Label required>Branch Code</Label>
              <Input
                id="prefixCode"
                {...register("prefixCode")}
                placeholder="E.g; ABC123"
                disabled={enableEdit}
              />
              {errors.prefixCode && (
                <p className="text-red-600 text-sm">
                  {errors.prefixCode.message}
                </p>
              )}
            </div>
            <div className="col-span-2">
              <Label required>Branch Name</Label>
              <Input
                id="name"
                {...register("name")}
                placeholder="E.g; Branch A"
                disabled={enableEdit}
              />
              {errors.name && (
                <p className="text-red-600 text-sm">{errors.name.message}</p>
              )}
            </div>
            <div>
              <Label required>Contact Number</Label>
              <Input
                id="contactNumber"
                {...register("contactNumber")}
                placeholder="E.g; 082617282"
                disabled={enableEdit}
              />
              {errors.contactNumber && (
                <p className="text-red-600 text-sm">
                  {errors.contactNumber.message}
                </p>
              )}
            </div>
            <div>
              <Label required>Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="example@company.com"
                disabled={enableEdit}
                {...register("email")}
              />
              {errors.email && (
                <p className="text-red-600 text-sm">{errors.email.message}</p>
              )}
            </div>
            <div className="col-span-2">
              <Label required>Address Line 1</Label>
              <Input
                id="address.addressLine0"
                {...register("address.addressLine0")}
                placeholder="3rd Miles Street, No. 9"
                disabled={enableEdit}
              />
              {errors.address?.addressLine0 && (
                <p className="text-red-600 text-sm">
                  {errors.address.addressLine0.message}
                </p>
              )}
            </div>
            <div className="col-span-2">
              <Label>Address Line 2</Label>
              <Input
                id="address.addressLine1"
                {...register("address.addressLine1")}
                placeholder="(Optional)"
                disabled={enableEdit}
              />
              {errors.address?.addressLine1 && (
                <p className="text-red-600 text-sm">
                  {errors.address.addressLine1.message}
                </p>
              )}
            </div>
            <div className="col-span-2">
              <Label>Address Line 3</Label>
              <Input
                id="address.addressLine2"
                {...register("address.addressLine2")}
                placeholder="(Optional)"
                disabled={enableEdit}
              />
              {errors.address?.addressLine2 && (
                <p className="text-red-600 text-sm">
                  {errors.address.addressLine2.message}
                </p>
              )}
            </div>
            <div>
              <Label required>City</Label>
              <Input
                id="address.cityName"
                {...register("address.cityName")}
                disabled={enableEdit}
              />
              {errors.address?.cityName && (
                <p className="text-red-600 text-sm">
                  {errors.address.cityName.message}
                </p>
              )}
            </div>
            <div>
              <Label>Postcode</Label>
              <Input
                id="address.postalZone"
                {...register("address.postalZone")}
                disabled={enableEdit}
              />
              {errors.address?.postalZone && (
                <p className="text-red-600 text-sm">
                  {errors.address.postalZone.message}
                </p>
              )}
            </div>
            <div>
              <Label required>State</Label>
              <Controller
                name="address.state"
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={enableEdit}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select State" />
                    </SelectTrigger>
                    <SelectContent>
                      {states.map((state) => (
                        <SelectItem key={state.Code} value={state.Code}>
                          {state.State}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.address?.state && (
                <p className="text-red-600 text-sm mt-1">
                  {errors.address?.state.message}
                </p>
              )}
            </div>
            <div>
              <Label required>Country</Label>
              <Controller
                name="address.country"
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={field.onChange}
                    value={field.value}
                    disabled={enableEdit}
                  >
                    <SelectTrigger className="h-10">
                      <SelectValue placeholder="Select Country" />
                    </SelectTrigger>
                    <SelectContent>
                      {countries.map((country) => (
                        <SelectItem key={country.Code} value={country.Code}>
                          {country.Country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
              {errors.address?.country && (
                <p className="text-red-600 text-sm mt-1">
                  {errors.address?.country.message}
                </p>
              )}
            </div>
            {currentActiveBranch?.role === "owner" &&
              (enableEdit && selectedBranch !== "CreateBranch" ? (
                <div className="col-span-2">
                  <Button
                    type="button"
                    className="w-full"
                    onClick={(e) => {
                      e.preventDefault();
                      handleEnableEdit();
                    }}
                  >
                    <Edit />
                    Edit
                  </Button>
                </div>
              ) : (
                <div className="col-span-2">
                  <Button
                    className="w-full"
                    type="submit"
                    disabled={isCreating}
                  >
                    {isCreating ? (
                      "Saving..."
                    ) : selectedBranch === "CreateBranch" ? (
                      <>
                        <Plus /> Create
                      </>
                    ) : (
                      <>
                        <Save /> Save
                      </>
                    )}
                  </Button>
                </div>
              ))}
          </form>
        )}
      </SheetContent>
    </Sheet>
  );
};

const BranchMembers = ({
  isOpen,
  onClose,
  selectedBranch,
  currentActiveBranch,
}: BranchModalProps) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { branchMembers, loadingMembers, removeMember, isRemoving } = useBranch(
    selectedBranch,
    undefined,
    searchTerm
  );
  const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
  const [deleteIndex, setDeleteIndex] = useState<number>(0);

  const handleRemoveMember = async (userId: string, userEmail: string) => {
    const memberToRemove = {
      userId: userId,
    };

    await removeMember(memberToRemove, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "branches" ||
            query.queryKey[0] === "branchMembers" ||
            query.queryKey[0] === "businessMembers",
        });
        setConfirmDelete(false);
        onClose();
        toast({
          title: "Member removed",
          description: `${userEmail} has been removed from the branch`,
          icon: (
            <CheckCircle
              size={30}
              className="animate-spin-once text-green-400"
            />
          ),
          duration: 5000,
        });
      },
      onError: (error) => {
        let errorMessage = `Failed to remove ${userEmail} from branch. Please try again.`;

        if (error instanceof AxiosError) {
          if (error.status === 422) {
            errorMessage = error.response?.data.message;
          }
        }

        toast({
          title: "Action Failed",
          variant: "destructive",
          description: errorMessage,
          icon: (
            <CircleAlert size={30} className="animate-spin-once text-red-400" />
          ),
          duration: 4000,
        });
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="h-fit max-h-[90vh] max-w-4xl flex flex-col">
        <DialogHeader>
          <DialogTitle>Branch Members</DialogTitle>
          <DialogDescription>View members of related branch</DialogDescription>
        </DialogHeader>
        <div className="w-full px-2.5">
          <Input
            className="w-full md:w-1/2"
            placeholder="Search email.."
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="w-full flex-1 grid grid-cols-3 text-sm gap-4 overflow-y-auto p-2.5">
          {loadingMembers ? (
            <div className="col-span-3 p-8">
              <LoadingIndicator />
            </div>
          ) : (
            branchMembers.map((member, index) => (
              <div
                key={member.id}
                className={`w-full border border-gray-100 shadow rounded-md p-2.5 flex ${
                  confirmDelete && deleteIndex === index
                    ? "h-full"
                    : "h-fit items-center justify-between gap-1.5"
                }`}
              >
                {confirmDelete && deleteIndex === index ? (
                  <div className="w-full h-full animate-slide-left flex flex-col gap-2.5">
                    <span className="w-full break-words">
                      Remove{" "}
                      <span className="font-semibold break-words">
                        {member.email}
                      </span>{" "}
                      from branch?
                    </span>
                    <div className="w-full flex">
                      <Button
                        className="w-full"
                        size="sm"
                        variant="ghost"
                        onClick={() => setConfirmDelete(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        className="w-full"
                        size="sm"
                        variant="destructive"
                        onClick={() =>
                          handleRemoveMember(member.id, member.email)
                        }
                        disabled={isRemoving}
                      >
                        {isRemoving ? "Removing.." : "Confirm"}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="h-full flex items-center gap-2.5 max-w-full leading-none">
                      <div>
                        <UserCircle2 color="gray" />
                      </div>
                      <span className="inline-block break-all">
                        {member.email}
                      </span>
                    </div>
                    {currentActiveBranch?.role === "owner" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="flex-shrink-0"
                        onClick={() => {
                          setDeleteIndex(index);
                          setConfirmDelete(true);
                        }}
                        disabled={confirmDelete}
                      >
                        <X />
                      </Button>
                    )}
                  </>
                )}
              </div>
            ))
          )}
        </div>
        <DialogFooter>
          <div className="w-full h-fit flex justify-center space-x-4">
            <Button size="sm" variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const RemoveBranch = ({
  isOpen,
  onClose,
  selectedBranch,
}: BranchModalProps) => {
  const { toast } = useToast();
  const { removeBranch, isRemoving } = useBranch(selectedBranch);
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<DeletionForm>({
    resolver: zodResolver(deletionSchema),
    defaultValues: {
      deletionReason: "",
    },
  });

  const [deletionReason] = watch(["deletionReason"]);

  const onSubmit = async (data: DeletionForm) => {
    await removeBranch(data, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["branches"] });
        onClose();
        reset();
        toast({
          title: "Branch removed",
          description: "Branch has been successfully removed",
          icon: (
            <CheckCircle
              size={30}
              className="animate-spin-once text-green-400"
            />
          ),
          duration: 5000,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Action Failed",
          description: error?.response?.data?.message || error?.message || "Failed to remove branch. Please try again.",
          variant: "destructive",
          icon: (
            <CircleAlert size={30} className="animate-spin-once text-red-400" />
          ),
          duration: 4000,
        });
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirmation</DialogTitle>
          <DialogDescription>
            Please provide reason to confirm the branch removal. Once confirmed,
            this action cannot be undone.
          </DialogDescription>
        </DialogHeader>
        <form
          id="deletionForm"
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          <div>
            <Input
              id="deletionReason"
              {...register("deletionReason")}
              placeholder="Enter reason..."
            />
            {errors.deletionReason && (
              <p className="text-red-600 text-sm">
                {errors.deletionReason.message}
              </p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" size="sm" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button
              form="deletionForm"
              type="submit"
              size="sm"
              variant="destructive"
              disabled={deletionReason === "" || isRemoving}
            >
              {isRemoving ? "Removing.." : "Remove"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const AddBranchMembers = ({
  isOpen,
  onClose,
  selectedBranch,
}: BranchModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const { addMember, isLoadingBusinessMembers, businessMembers } = useBranch(
    selectedBranch,
    undefined,
    searchTerm
  );

  const handleAddMember = async (user: BranchMember) => {
    const selectedMember = user.email;
    const data = {
      userId: user.id,
    };

    await addMember(data, {
      onSuccess: () => {
        queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === "branches" ||
            query.queryKey[0] === "branchMembers" ||
            query.queryKey[0] === "businessMembers",
        });
        onClose();
        toast({
          title: "Member added",
          description: `${selectedMember} has been added to branch`,
          icon: (
            <CheckCircle
              size={30}
              className="animate-spin-once text-green-400"
            />
          ),
          duration: 5000,
        });
      },
      onError: (error: any) => {
        toast({
          title: "Action Failed",
          variant: "destructive",
          description: error?.response?.data?.message || error?.message || `Failed to add ${selectedMember} to branch. Please try again.`,
          icon: (
            <CircleAlert size={30} className="animate-spin-once text-red-400" />
          ),
          duration: 4000,
        });
      },
    });
  };
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="h-fit max-h-[90vh] max-w-4xl flex flex-col">
        <DialogHeader>
          <DialogTitle>Add Members</DialogTitle>
          <DialogDescription>Select members to add</DialogDescription>
        </DialogHeader>
        <div className="w-full flex flex-col">
          <Input
            placeholder="Search email.."
            className="w-full md:w-1/2"
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="w-full flex-1 grid grid-cols-2 md:grid-cols-3 text-sm gap-4 overflow-y-auto p-2.5">
          {businessMembers && businessMembers.length > 0 ? (
            businessMembers.map((m) => (
              <div
                key={m.id}
                className={`w-full h-full border border-gray-100 shadow rounded-md p-2.5 flex justify-between gap-1.5`}
              >
                <div className="flex items-center gap-2.5 max-w-full leading-none">
                  <div>
                    <UserCircle2 color="gray" />
                  </div>
                  <span className="inline-block break-all">{m.email}</span>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="flex-shrink-0"
                  onClick={() => handleAddMember(m)}
                >
                  <Plus />
                </Button>
              </div>
            ))
          ) : isLoadingBusinessMembers ? (
            <div className="col-span-3">
              <LoadingIndicator />
            </div>
          ) : (
            <div className="col-span-3 text-gray-400 w-full flex flex-col items-center justify-center">
              <UserCheck2 size={50} />
              <span>All business members are in this branch</span>
            </div>
          )}
        </div>
        <DialogFooter>
          <div className="w-full h-fit flex justify-center space-x-4">
            <Button size="sm" type="button" variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SwitchActiveBranch = ({
  isOpen,
  onClose,
  selectedBranch,
}: BranchModalProps) => {
  const { toast } = useToast();
  const { changeBranch } = useBranch(selectedBranch);
  const queryClient = useQueryClient();

  const handleChangingBranch = async () => {
    const data: ChangeActiveBranch = {
      branchId: selectedBranch,
    };

    await changeBranch(data, {
      onSuccess: () => {
        queryClient.invalidateQueries();
        onClose();

        toast({
          title: "Switched Branch",
          description: `Successfully switch branch.`,
          icon: (
            <CheckCircle
              size={30}
              className="animate-spin-once text-green-400"
            />
          ),
          duration: 5000,
        });
      },
      onError: (error) => {
        let errorMessage = "Failed to switch branch. Please try again.";

        if (error instanceof AxiosError) {
          errorMessage = error.response?.data.message;
        }

        toast({
          title: "Action Failed",
          variant: "destructive",
          description: errorMessage,
          icon: (
            <CircleAlert size={30} className="animate-spin-once text-red-400" />
          ),
          duration: 4000,
        });
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirmation</DialogTitle>
          <DialogDescription>Change to active branch?</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button type="button" size="sm" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={handleChangingBranch}>
            Change
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const SelfServiceUrlDialog = ({
  isOpen,
  onClose,
  currentActiveBranch,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentActiveBranch?: UserActiveBranch;
}) => {
  const { toast } = useToast();
  const { user } = useAuth();

  if (!currentActiveBranch || !user) {
    return null;
  }

  const selfServiceUrl = `${window.location.origin}/self-service?businessId=${user.businessId}&branchId=${currentActiveBranch.branchId}`;

  const copyToClipboard = () => {
    navigator.clipboard.writeText(selfServiceUrl);
    toast({
      title: "Link Copied!",
      description: "Self-service URL has been copied to clipboard",
      icon: (
        <CheckCircle
          size={30}
          className="animate-spin-once text-green-400"
        />
      ),
      duration: 3000,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Customer Self-Service Portal URL</DialogTitle>
          <DialogDescription>
            Share this link with your customers so they can submit their invoice details directly
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-100 p-4 rounded-md dark:bg-blue-950/20 dark:border-blue-900/50">
            <p className="text-sm font-semibold text-blue-900 mb-2">
              Active Branch: {currentActiveBranch.branchName}
            </p>
            <p className="text-xs text-blue-700">
              This URL is specific to your currently active branch. Customers using this link will submit invoices to this branch.
            </p>
          </div>

          <div>
            <Label>Self-Service URL</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={selfServiceUrl}
                readOnly
                className="font-mono text-sm"
              />
              <Button
                type="button"
                onClick={copyToClipboard}
                variant="outline"
              >
                <Copy />
                Copy
              </Button>
            </div>
          </div>

          <div className="bg-orange-50 border border-orange-100 p-4 rounded-md dark:bg-orange-950/20 dark:border-orange-900/50">
            <p className="text-sm font-semibold text-orange-900 mb-2">
              How it works:
            </p>
            <ol className="text-xs text-orange-700 space-y-1 list-decimal list-inside">
              <li>Share this URL with your customers via email, SMS, or your website</li>
              <li>Customers will enter their POS Reference Number, Invoice Date, and Total Amount</li>
              <li>They will fill in their personal information (buyer details)</li>
              <li>Upon submission, the invoice will be automatically validated and sent to LHDN</li>
            </ol>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default Branch;
